import { useEffect, useRef, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { Loader2, Sparkles } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { inputBase } from '@/components/ui/input';
import { api, type ApiError } from '@/lib/api';
import type { AiContentResponse } from '@/lib/types';
import { cn } from '@/lib/utils';

// S4 Q&A answer flow — the ReviewReplyBox skeleton (S3 §4.2), Q&A edition:
// "Answer with AI" spends 1 AI help and only fills the box (no server-side
// draft state — the design's column set is the spec); "Post answer" is the
// separate owner action that reaches Google. The textarea is always
// editable and no failure path clears it; a 'failed' answer arrives here
// prefilled from the server-preserved text.

const MAX_ANSWER = 1500;

// Server error code → plain sentence. Never render a raw code.
const ANSWER_ERRORS: Record<string, string> = {
  quota_exceeded:
    "You've used all your AI helps for this month. You can still write and post an answer yourself, or add more helps from Billing.",
  compliance_refused:
    "We couldn't draft this one — the draft came out with wording Google doesn't allow. Try writing it yourself.",
  answer_rejected: "Google didn't accept this answer. Edit the text and try again.",
  reauth_needed:
    'Your Google connection needs to be renewed. Reconnect from the top of this page, then try again.',
  already_answered: 'This question already has your posted answer.',
  send_in_progress: 'That answer is already being posted — give it a second and refresh.',
  locked: "Your current plan doesn't include this anymore. Check your plan in Billing.",
  not_verified: "Google hasn't finished verifying this listing yet, so changes can't be published.",
  not_connected: "This site's Google connection was removed. Reconnect from the top of this page.",
  gbp_unavailable: "Google features aren't available right now. Try again later.",
};
const ANSWER_ERROR_FALLBACK =
  "That didn't go through. Your text is saved here — try again in a moment.";
function answerErrorMessage(code: string | undefined): string {
  return (code && ANSWER_ERRORS[code]) ?? ANSWER_ERROR_FALLBACK;
}

function apiErrorCode(err: unknown): string | undefined {
  const body = (err as ApiError | undefined)?.body;
  return body && typeof body === 'object' && 'error' in body
    ? String((body as { error: unknown }).error)
    : undefined;
}

// T12/B2: the ONE input recipe (ui/input.tsx's `inputBase`) plus the
// textarea-only sizing.
const textareaClasses = cn(inputBase, 'py-2 leading-relaxed resize-y min-h-[6rem]');

export function QuestionAnswerBox(props: {
  siteId: string;
  questionId: string;
  /** A 'failed' answer's server-preserved text, prefilled for retry. */
  initialText: string | null;
  canWrite: boolean;
  onChanged: (announcement?: string) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const { siteId, questionId, initialText, canWrite, onChanged } = props;
  const [open, setOpen] = useState(initialText !== null);
  const [text, setText] = useState(initialText ?? '');
  const [busy, setBusy] = useState<'draft' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // review-reply-box.tsx's focusPending mechanism, verbatim: a landed draft
  // must move focus into the textarea or a keyboard/SR user never learns the
  // text arrived. Never true on mount.
  const focusPending = useRef(false);

  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [open, text]);

  // Addendum §A correction: sibling controls (ReviewReplyBox's !canReply,
  // GbpPostCompose's !canWrite) both explain the locked state instead of
  // vanishing — the brief's original `return null` here was the odd one
  // out. Same gate (hasVoiceOfMerchant), same sentence family.
  if (!canWrite) {
    return (
      <p className="mt-3 text-sm text-text-tertiary">
        Answering unlocks once Google finishes verifying this listing.
      </p>
    );
  }

  const errorId = `qa-${questionId}-error`;
  const basePath = locationApi(businessId, siteId, `gbp/questions/${encodeURIComponent(questionId)}`);

  async function draftWithAi() {
    setBusy('draft');
    setError(null);
    try {
      const res = await api.post<AiContentResponse<{ answer: string }>>(`${basePath}/draft`, {});
      setText(res.result.answer);
      setOpen(true);
      focusPending.current = true;
    } catch (err) {
      setError(answerErrorMessage(apiErrorCode(err)));
    } finally {
      setBusy(null);
    }
  }

  async function postAnswer() {
    setBusy('send');
    setError(null);
    try {
      await api.post(`${basePath}/answer`, { text });
      onChanged('Answer posted to Google.');
    } catch (err) {
      setError(answerErrorMessage(apiErrorCode(err)));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      {open ? (
        <>
          <label
            className="eyebrow"
            htmlFor={`qa-${questionId}`}
          >
            Your answer
          </label>
          <textarea
            ref={textareaRef}
            id={`qa-${questionId}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={MAX_ANSWER}
            rows={4}
            aria-describedby={error ? errorId : undefined}
            className={textareaClasses}
          />
          <p className="text-xs text-text-tertiary">Nothing goes to Google until you post it.</p>
          {error && (
            <p id={errorId} className="text-sm text-danger-fg" role="alert">
              {error}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy !== null || text.trim().length === 0}
              onClick={() => void postAnswer()}
              className={buttonVariants({ size: 'sm' })}
            >
              {busy === 'send' ? 'Posting…' : 'Post answer'}
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void draftWithAi()}
              className={buttonVariants({ size: 'sm', variant: 'outline' })}
            >
              {busy === 'draft' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
              )}
              {busy === 'draft' ? 'Drafting…' : 'Redraft with AI'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {/* Design audit T12 → T14: outline, not filled. This button
                repeats once per unanswered question — a list action, and a
                list of primaries is a list of none. Same call the review
                reply box already got. */}
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void draftWithAi()}
              className={buttonVariants({ size: 'sm', variant: 'outline' })}
            >
              {busy === 'draft' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
              )}
              {busy === 'draft' ? 'Drafting…' : 'Answer with AI'}
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setOpen(true);
                focusPending.current = true;
              }}
              className={buttonVariants({ size: 'sm', variant: 'ghost' })}
            >
              Write it myself
            </button>
          </div>
          {error && (
            <p className="text-sm text-danger-fg" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
